import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAddUserToDistrictManager } from "@/api/reseller/mutations";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { addUserToManagerSchema } from "@/lib/schemas/manager.schema";
import { getDrfFieldErrors } from "@/utils/errors";

type AddUserDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	districtManagerId: number;
	companyName?: string;
};

export function AddUserDialog({
	open,
	onOpenChange,
	districtManagerId,
	companyName,
}: AddUserDialogProps) {
	const { t } = useTranslation();
	const [email, setEmail] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const addUser = useAddUserToDistrictManager();

	useEffect(() => {
		if (open) {
			setEmail("");
			setFieldErrors({});
		}
	}, [open]);

	function handleSubmit() {
		setFieldErrors({});
		const result = addUserToManagerSchema.safeParse({ email: email.trim() });
		if (!result.success) {
			toast.error(
				result.error.issues[0]?.message ?? t("common.validationError"),
			);
			return;
		}
		addUser.mutate(
			{ district_manager_id: districtManagerId, email: email.trim() },
			{
				onSuccess: () => {
					toast.success(t("managers.addUserSuccess"));
					onOpenChange(false);
				},
				onError: (error) => {
					if (error.message === "rate_limited") return;
					setFieldErrors(getDrfFieldErrors(error));
					toast.error(t("managers.addUserError"));
				},
			},
		);
	}

	const canSubmit =
		email.trim() && !addUser.isPending && !addUser.isRateLimited;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Dodaj użytkownika</DialogTitle>
					<DialogDescription>
						{companyName
							? `Dodaj nowego użytkownika do zarządcy: ${companyName}`
							: "Podaj e-mail nowego lub istniejącego użytkownika"}
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-4 py-2">
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="add-user-email">E-mail</Label>
						<Input
							id="add-user-email"
							type="email"
							name="email"
							autoComplete="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder="np. jan@firma.pl"
							onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
							aria-invalid={!!fieldErrors.email}
							aria-describedby={
								fieldErrors.email ? "add-user-email-error" : undefined
							}
						/>
						{fieldErrors.email && (
							<p
								id="add-user-email-error"
								className="text-destructive text-sm"
								role="alert"
							>
								{fieldErrors.email}
							</p>
						)}
					</div>
				</div>

				<DialogFooter>
					<Button
						size="sm"
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={addUser.isPending}
					>
						Anuluj
					</Button>
					<Button onClick={handleSubmit} disabled={!canSubmit}>
						{addUser.isPending ? "Dodawanie…" : "Dodaj"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
